import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import { showError, showSuccess } from "redux/actions/ui";
import { TTaxLines } from "redux/reducers/models/facility";

import { displayPercent } from "helpers/Helpers";
import { useAppDispatch, useAppSelector } from "hooks/redux";

import Page from "components/page/Page";
import Card from "components/card/Card";
import DataTable from "../../../../../components/dataTable/DataTable";

import { useTranslation } from "react-i18next";
import { GetTaxLine, PutArchiveTaxLine } from "api/rpc/2024-04/facilityAdmin/client/taxLines";
import { GetTaxNumbers } from "api/rpc/2024-04/facilityAdmin/facility/taxNumber";
import axios, { CancelToken } from "axios";

import { Badge } from "components/badge/Badge";
import "../facility/facility.scss";
import { StatusCode } from "api/protocols";
import { GetProvince } from "api/rpc/2024-04/country";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArchive } from "@fortawesome/pro-regular-svg-icons";
import ArchiveTaxLine from "components/archivePopup/archivePopup";

interface ITaxNumber {
  id: number;
  title: string;
  number: string;
}

interface IArchivePopup {
  isOpen: boolean;
  selectedTaxLineID?: number;
}

const CURRENT_URL = "/admin/settings/taxes";
export default function TaxLines() {
  const history = useHistory();
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const { facilityStore, authStore } = useAppSelector(store => store);

  const [taxLines, setTaxLines] = useState<TTaxLines[]>(undefined);
  const [taxNumbers, setTaxNumbers] = useState<ITaxNumber[]>(undefined);

  const [archiveWarningState, setArchiveWarningState] = useState<IArchivePopup>({
    isOpen: false,
    selectedTaxLineID: null,
  });

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadTaxLines(source.token);
    return () => source.cancel("TaxLines unmounted.");
  }, [facilityStore.facility]);

  async function loadTaxLines(token?: CancelToken) {
    if (taxLines !== undefined) {
      setTaxLines(undefined);
    }
    const res = await GetTaxLine(null, token ? false : true, token);

    if (token && token.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading taxlines.")));
    }

    const numberRes = await GetTaxNumbers(null, token ? false : true, token);

    if (token && token.reason) {
      return;
    }
    if (numberRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading tax numbers.")));
    }

    const params = {
      country_id: facilityStore?.facility?.country_id,
    };
    const provinceRes = await GetProvince(params, false);
    if (provinceRes.status !== StatusCode.OK) {
      return;
    }

    const provinces = provinceRes.data;
    const taxLinesRes = res.data;

    for (let i = 0; i < taxLinesRes?.length; i++) {
      for (let k = 0; k < provinces?.length; k++) {
        if (taxLinesRes[i].province_id === provinces[k].id) {
          taxLinesRes[i].province_name = provinces[k].name;
        }
      }
    }

    setTaxLines(res.status !== StatusCode.OK ? [] : taxLinesRes);
    setTaxNumbers(numberRes.data as ITaxNumber[]);
  }

  async function handleArchiveTaxLine() {
    const archiveTaxLineRes = await PutArchiveTaxLine({ tax_line_id: archiveWarningState.selectedTaxLineID }, true);

    if (archiveTaxLineRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error archiving tax line")));
      return;
    }

    dispatch(showSuccess(t("Tax line successfully archived")));
    setArchiveWarningState({ isOpen: false, selectedTaxLineID: null });
    void loadTaxLines();
  }

  function handleOpenTaxLineArchive(e: React.MouseEvent<SVGSVGElement, MouseEvent>, tax_line_id: number) {
    e.stopPropagation();
    setArchiveWarningState(prev => ({ ...prev, isOpen: !prev.isOpen, selectedTaxLineID: tax_line_id }));
  }

  return (
    <Page
      title={t("Taxes")}
      narrow
      multipleActionDropdownAction={{
        label: t("Options"),
        dropdownProps: {
          alignment: "right",
          options: [
            {
              type: "handler",
              label: t("Add Tax Number"),
              handler: () => history.push(CURRENT_URL + "/number/new"),
              icon: "hashtag",
            },
            {
              type: "handler",
              label: t("Add Tax Line"),
              handler: () => history.push(CURRENT_URL + "/line/new"),
              icon: "percent",
            },
          ],
        },
      }}
    >
      <Card>
        <Card.Section title={t("Tax Numbers")}>
          <DataTable columns={[{ label: t("Title") }, { label: t("Number") }]} loading={taxNumbers === undefined}>
            {taxNumbers?.map(number => (
              <tr
                key={number.id}
                onClick={() => history.push(CURRENT_URL + `/number/${String(number.id)}`)}
                className="clickable"
              >
                <td>{number.title}</td>
                <td>{number.number}</td>
              </tr>
            ))}
          </DataTable>
        </Card.Section>
      </Card>
      <Card>
        <Card.Section title={t("Tax Lines")}>
          <DataTable
            columns={[
              { label: t("Name") },
              { label: t("Percentage") },
              { label: t("Province") },
              ...(authStore?.user?.permissions?.tax_lines_archive ? [{ label: "", width: "5%" }] : []),
            ]}
            loading={taxLines === undefined}
          >
            {taxLines?.map(line => (
              <tr
                key={line.id}
                onClick={() => history.push(CURRENT_URL + `/line/${String(line.id)}`)}
                className="clickable"
              >
                <td>
                  {line.name}{" "}
                  {line.compound ? (
                    <Badge size="medium" type="primary">
                      {t("Compound")}
                    </Badge>
                  ) : (
                    ""
                  )}
                </td>
                <td>{displayPercent(line.percentage / 100, 3)}</td>
                <td>
                  <Badge size="medium" type="primary">
                    {line.province_name}
                  </Badge>
                </td>
                {authStore?.user?.permissions?.tax_lines_archive ? (
                  <td>
                    <FontAwesomeIcon icon={faArchive} size={"lg"} onClick={e => handleOpenTaxLineArchive(e, line.id)} />
                  </td>
                ) : null}
              </tr>
            ))}
          </DataTable>
        </Card.Section>
      </Card>

      <ArchiveTaxLine
        isOpen={archiveWarningState.isOpen}
        title={t("Archive tax line?")}
        onOk={handleArchiveTaxLine}
        onCancel={() => setArchiveWarningState({ isOpen: false, selectedTaxLineID: null })}
      />
    </Page>
  );
}
